import React from "react";
import "regenerator-runtime/runtime";
import { v4 as uuidv4 } from "uuid";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { MdAutoAwesome, MdClose, MdSend, MdRestartAlt, MdMic, MdMicOff, MdThumbUp, MdThumbDown } from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import { endpoints } from "../../Utils/Constants";
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from "../../Utils/Permissions";
import { streamAnalyticsChat, emailAnalyticsChatSummary } from "../../Services/AnalyticsChatService";
import { submitAiFeedback } from "../../Services/AiFeedbackService";
import ChatMessageRenderer from "./ChatMessageRenderer";
import EndConversationModal from "./EndConversationModal";
import AiFeedbackActions from "../Common/AiFeedbackActions";
import "../../Styles/AnalyticsChat.css";

const MAX_HISTORY_MESSAGES = 20;
// Large reports stream as hundreds of small SSE deltas; re-rendering (and re-parsing the
// whole markdown message) on every single one froze the tab. Coalesce into fixed-interval flushes.
const STREAM_FLUSH_MS = 80;
const FOLLOWUP_PREFIX = "[Suggested follow-up] ";
const TEXTAREA_MAX_HEIGHT = 200;
const WELCOME_PROMPTS = [
    "How many patients did we see this month?",
    "Break down appointments by facility",
    "What's our no-show rate this quarter?",
];
const THINKING_MESSAGES = [
    "Thinking through your question...",
    "Retrieving the relevant data...",
    "Checking the latest analytics...",
    "Reviewing your organization's records...",
    "Connecting the dots across your data...",
    "Finding the most useful answer...",
    "Analyzing the current trends...",
    "Comparing the numbers for you...",
    "Looking across your appointments...",
    "Exploring the available insights...",
    "Calculating the key figures...",
    "Preparing a clear summary...",
    "Validating the results...",
    "Filtering the data to your request...",
    "Checking for meaningful patterns...",
    "Organizing the analytics...",
    "Measuring the relevant metrics...",
    "Putting the findings together...",
    "Almost ready with your answer...",
    "Finishing the data review...",
];

const THINKING_TAIL_CHARS = 140;
const extractThinkingHeadline = (text) => {
    if (!text) return "";
    const lines = text.split("\n").map((line) => line.trim()).filter(Boolean);
    const last = lines[lines.length - 1] || "";
    const cleaned = last.replace(/\*\*/g, "").replace(/^#+\s*/, "").trim();
    if (cleaned.length <= THINKING_TAIL_CHARS) return cleaned;
    const sentences = cleaned.match(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g) || [cleaned];
    let result = "";
    for (let i = sentences.length - 1; i >= 0; i -= 1) {
        const candidate = sentences[i] + result;
        if (result && candidate.trim().length > THINKING_TAIL_CHARS) break;
        result = candidate;
    }
    result = result.trim();
    return result.length < cleaned.length ? `…${result}` : result;
};

/** Streaming AI assistant panel; defaults are the organization analytics assistant, other assistants override the copy, endpoints and request context. */
export default function AnalyticsChatWidget({
    organizationId,
    organizationName,
    requestContext,
    chatEndpoint = endpoints.ANALYTICS_CHAT,
    summaryEndpoint = endpoints.ANALYTICS_CHAT_SUMMARY_EMAIL,
    permissionCode = PermissionCodes.ANALYTICS_AI_VIEW,
    title = "Analytics Assistant",
    subtitle,
    triggerLabel = "Ask Aegis AI",
    emptyTitle = "Ask me about your analytics",
    emptyDescription,
    welcomePrompts = WELCOME_PROMPTS,
    thinkingMessages = THINKING_MESSAGES,
    placeholder = "Ask about appointments, revenue, patients...",
    // { id, text }: opens the panel and asks `text`; a new id asks again (e.g. "Ask why" buttons on the page).
    launchRequest = null,
}) {
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );
    const canAccessAi = permissionHas(userPermissionCodes, permissionCode, decodedToken);
    const context = requestContext || { organization_id: organizationId || null };

    const [isOpen, setIsOpen] = React.useState(false);
    const [messages, setMessages] = React.useState([]);
    const [inputValue, setInputValue] = React.useState("");
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [streamingText, setStreamingText] = React.useState("");
    const [followUps, setFollowUps] = React.useState([]);
    const [panelWidth, setPanelWidth] = React.useState(null);
    const [showEndModal, setShowEndModal] = React.useState(false);
    const [isEndingConversation, setIsEndingConversation] = React.useState(false);
    const [endConversationError, setEndConversationError] = React.useState("");
    const [thinkingMessage, setThinkingMessage] = React.useState(thinkingMessages[0]);
    const [thinkingText, setThinkingText] = React.useState("");
    const thinkingAccumulator = React.useRef("");
    const [feedbackById, setFeedbackById] = React.useState({});

    React.useEffect(() => {
        if (!isStreaming) return undefined;

        const interval = setInterval(() => {
            setThinkingMessage((currentMessage) => {
                const currentIndex = thinkingMessages.indexOf(currentMessage);
                return thinkingMessages[(currentIndex + 1) % thinkingMessages.length];
            });
        }, 8000);

        return () => clearInterval(interval);
    }, [isStreaming, thinkingMessages]);

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    React.useEffect(() => {
        if (listening) {
            setInputValue(transcript);
        }
    }, [transcript, listening]);

    React.useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
        }
    }, [inputValue]);

    const toggleListening = () => {
        if (listening) {
            SpeechRecognition.stopListening();
        } else {
            resetTranscript();
            SpeechRecognition.startListening({ continuous: true });
        }
    };

    const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 992);
    const streamAccumulator = React.useRef("");
    const streamFlushTimer = React.useRef(null);
    const bodyRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const isDragging = React.useRef(false);
    const isUserScrolledUp = React.useRef(false);
    const turnId = React.useRef(0);
    // session_id is stable for the whole conversation, chat_id is new per user turn —
    // both generated here and sent with the request; the assistant message carries
    // them so the feedback can be keyed to the exact response.
    const sessionIdRef = React.useRef(uuidv4());

    React.useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 992);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    React.useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth > 320 && newWidth < window.innerWidth * 0.9) {
                setPanelWidth(newWidth);
            }
        };
        const handleMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            }
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const handleMouseDown = () => {
        if (isDesktop) {
            isDragging.current = true;
            document.body.style.cursor = "ew-resize";
            document.body.style.userSelect = "none";
        }
    };

    React.useEffect(() => {
        if (!isOpen) return undefined;
        const handleEscClose = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        window.addEventListener("keydown", handleEscClose);
        return () => window.removeEventListener("keydown", handleEscClose);
    }, [isOpen]);

    React.useEffect(() => {
        if (!bodyRef.current) return;
        if (!isUserScrolledUp.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, streamingText, isOpen]);

    React.useEffect(() => {
        if (!isOpen) return undefined;
        const timer = setTimeout(() => inputRef.current?.focus(), 250);
        return () => clearTimeout(timer);
    }, [isOpen]);

    React.useEffect(() => {
        return () => clearTimeout(streamFlushTimer.current);
    }, []);

    const clearStreamFlush = () => {
        clearTimeout(streamFlushTimer.current);
        streamFlushTimer.current = null;
    };

    const scheduleStreamFlush = () => {
        if (streamFlushTimer.current) return;
        streamFlushTimer.current = setTimeout(() => {
            streamFlushTimer.current = null;
            setStreamingText(streamAccumulator.current);
        }, STREAM_FLUSH_MS);
    };

    const sendMessage = async (overrideText, isFollowup = false) => {
        const trimmed = (overrideText ?? inputValue).trim();
        if (!trimmed || isStreaming || !token) return;

        if (listening) {
            SpeechRecognition.stopListening();
        }

        const myTurnId = ++turnId.current;
        const nextMessages = [...messages, { role: "user", content: trimmed, isFollowup }].slice(-MAX_HISTORY_MESSAGES);
        setMessages(nextMessages);
        setInputValue("");
        if (inputRef.current) inputRef.current.style.height = "auto";
        setIsStreaming(true);
        setFollowUps([]);
        clearStreamFlush();
        streamAccumulator.current = "";
        setStreamingText("");
        setThinkingMessage(thinkingMessages[0]);
        thinkingAccumulator.current = "";
        setThinkingText("");
        isUserScrolledUp.current = false;
        const sessionId = sessionIdRef.current;
        const chatId = uuidv4();
        let settled = false;
        const settleAnswer = () => {
            if (settled) return;
            settled = true;
            clearStreamFlush();
            const finalText = streamAccumulator.current.trim();
            setMessages((prev) =>
                finalText
                    ? [...prev, { role: "assistant", content: finalText, sessionId, chatId }]
                    : prev
            );
            setStreamingText("");
            streamAccumulator.current = "";
            thinkingAccumulator.current = "";
            setThinkingText("");
            setIsStreaming(false);
        };

        const result = await streamAnalyticsChat(
            {
                messages: nextMessages.map((m) => ({
                    role: m.role,
                    content: m.isFollowup ? `${FOLLOWUP_PREFIX}${m.content}` : m.content,
                })),
                ...context,
                allow_email: false,
                from_suggestion: Boolean(isFollowup),
                session_id: sessionId,
                chat_id: chatId,
            },
            token,
            (chunk) => {
                thinkingAccumulator.current = "";
                setThinkingText("");
                streamAccumulator.current += chunk;
                scheduleStreamFlush();
            },
            null,
            settleAnswer,
            (questions) => {
                if (turnId.current === myTurnId) setFollowUps(questions);
            },
            () => {
                // Model streamed a tool-call preamble before the real answer — drop it.
                clearStreamFlush();
                streamAccumulator.current = "";
                setStreamingText("");
            },
            null,
            (delta) => {
                thinkingAccumulator.current += delta;
                setThinkingText(thinkingAccumulator.current);
            },
            chatEndpoint
        );
        settleAnswer(); // no-op if "done" already fired; covers error paths where it never does

        if (!result.is_success) {
            toast.error(result.message || `The ${title.toLowerCase()} ran into a problem.`);
        }
    };

    const sendMessageRef = React.useRef(sendMessage);
    sendMessageRef.current = sendMessage;
    React.useEffect(() => {
        if (!launchRequest?.id || !launchRequest?.text) return;
        setIsOpen(true);
        sendMessageRef.current(launchRequest.text, true);
    }, [launchRequest?.id, launchRequest?.text]);

    const handleFeedback = async (sessionId, chatId, sentiment) => {
        if (!sessionId || !chatId || feedbackById[chatId] === sentiment) return;
        const previous = feedbackById[chatId];
        setFeedbackById((prev) => ({ ...prev, [chatId]: sentiment }));
        const res = await submitAiFeedback(sessionId, chatId, sentiment, token);
        if (!res?.is_success) {
            setFeedbackById((prev) => {
                const next = { ...prev };
                if (previous) next[chatId] = previous;
                else delete next[chatId];
                return next;
            });
            toast.error(res?.message || "Couldn't record that feedback.");
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (listening) {
            SpeechRecognition.stopListening();
        }
        sendMessage();
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    const handleScroll = () => {
        if (!bodyRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
        isUserScrolledUp.current = scrollHeight - scrollTop - clientHeight > 50;
    };

    const handleTextareaChange = (event) => {
        setInputValue(event.target.value);
    };

    const handleReset = () => {
        if (isStreaming) return;
        turnId.current++; // invalidates any in-flight turn's late-arriving follow-ups callback
        setMessages([]);
        setStreamingText("");
        setFollowUps([]);
        setFeedbackById({});
        streamAccumulator.current = "";
        thinkingAccumulator.current = "";
        setThinkingText("");
        sessionIdRef.current = uuidv4(); // a new conversation is a new telemetry session
        isUserScrolledUp.current = false;
    };

    const handleConfirmEndConversation = async (sendEmailSummary) => {
        if (!sendEmailSummary || !summaryEndpoint) {
            setShowEndModal(false);
            handleReset();
            return;
        }

        setIsEndingConversation(true);
        setEndConversationError("");
        const result = await emailAnalyticsChatSummary(
            {
                messages: messages.map((m) => ({
                    role: m.role,
                    content: m.isFollowup ? `${FOLLOWUP_PREFIX}${m.content}` : m.content,
                })),
                ...context,
            },
            token,
            summaryEndpoint
        );
        setIsEndingConversation(false);

        if (!result.is_success) {
            setEndConversationError(
                result.message || "Couldn't send the summary email. You can still end the conversation."
            );
            return;
        }

        toast.success(result.message || "A summary of this conversation will be emailed to you shortly.");
        setShowEndModal(false);
        handleReset();
    };

    if (!canAccessAi) {
        return null;
    }

    const showEmptyState = messages.length === 0 && !streamingText;

    return (
        <>
            <button
                type="button"
                className="hc-chat-trigger d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold border-0 shadow-sm"
                onClick={() => setIsOpen(true)}
                style={{ minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}
            >
                <MdAutoAwesome size={18} />
                <span>{triggerLabel}</span>
            </button>

            <div
                className={`offcanvas offcanvas-end hc-chat-offcanvas ${isOpen ? "show" : ""}`}
                style={{
                    visibility: isOpen ? "visible" : "hidden",
                    zIndex: 1050,
                    ...(isDesktop && panelWidth ? { width: `${panelWidth}px` } : {})
                }}
                aria-hidden={!isOpen}
            >
                <div
                    className="hc-chat-resizer d-none d-lg-block"
                    onMouseDown={handleMouseDown}
                    style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "6px",
                        cursor: "ew-resize",
                        zIndex: 10,
                        backgroundColor: "transparent"
                    }}
                />
                <div className="hc-chat-header d-flex align-items-center justify-content-between px-4 py-3">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        <span className="hc-chat-header-icon">
                            <MdAutoAwesome size={19} />
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <h5 className="mb-0 fw-bold text-white text-truncate">{title}</h5>
                            <small className="hc-chat-header-sub text-truncate d-block">
                                {subtitle || organizationName || "Ask about your data"}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {messages.length > 0 && (
                            <button
                                type="button"
                                className="hc-chat-icon-btn"
                                onClick={() => {
                                    if (isStreaming) return;
                                    setEndConversationError("");
                                    setShowEndModal(true);
                                }}
                                title="End conversation"
                                aria-label="End conversation"
                            >
                                <MdRestartAlt size={17} />
                            </button>
                        )}
                        <button
                            type="button"
                            className="hc-chat-icon-btn"
                            onClick={() => setIsOpen(false)}
                            aria-label="Close analytics assistant"
                        >
                            <MdClose size={18} />
                        </button>
                    </div>
                </div>

                <div className="offcanvas-body hc-chat-body p-0 d-flex flex-column">
                    <div className="hc-chat-messages flex-grow-1" ref={bodyRef} onScroll={handleScroll}>
                        {showEmptyState ? (
                            <div className="hc-chat-empty">
                                <div className="hc-chat-empty-icon">
                                    <MdAutoAwesome size={26} />
                                </div>
                                <p className="mb-1 fw-semibold text-dark">{emptyTitle}</p>
                                <small className="text-muted d-block mb-3">
                                    {emptyDescription || (
                                        <>
                                            Appointments, revenue, patient trends, providers and more — for{" "}
                                            {organizationName || "this organization"}.
                                        </>
                                    )}
                                </small>
                                <div className="d-flex flex-column gap-2 w-100">
                                    {welcomePrompts.map((prompt) => (
                                        <button
                                            key={prompt}
                                            type="button"
                                            className="hc-chat-suggestion"
                                            onClick={() => sendMessage(prompt, true)}
                                        >
                                            {prompt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-3 p-3">
                                {messages.map((message, index) => (
                                    <div
                                        key={index}
                                        className={`hc-chat-bubble ${message.role === "user" ? "hc-chat-bubble-user" : "hc-chat-bubble-assistant"
                                            }`}
                                    >
                                        {message.role === "user" ? (
                                            message.content
                                        ) : (
                                            <>
                                                <ChatMessageRenderer content={message.content} />
                                                {message.chatId && (
                                                    <AiFeedbackActions
                                                        feedback={feedbackById[message.chatId]}
                                                        onFeedback={(sentiment) =>
                                                            handleFeedback(message.sessionId, message.chatId, sentiment)
                                                        }
                                                        size="sm"
                                                        variant="chat"
                                                        label="Was this helpful?"
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                                {isStreaming && !streamingText.trim() && (
                                    <div className="hc-chat-thinking-standalone" aria-live="polite">
                                        <span className="hc-chat-loader">
                                            <span />
                                            <span />
                                            <span />
                                        </span>
                                        <span className="hc-chat-thinking text-muted fst-italic">
                                            {extractThinkingHeadline(thinkingText) || thinkingMessage}
                                        </span>
                                    </div>
                                )}
                                {isStreaming && streamingText.trim() && (
                                    <div className="hc-chat-bubble hc-chat-bubble-assistant">
                                        <ChatMessageRenderer content={streamingText} isStreaming />
                                        <div className="hc-chat-status-row hc-chat-status-row-with-response" aria-live="polite">
                                            <span className="hc-chat-loader">
                                                <span />
                                                <span />
                                                <span />
                                            </span>
                                            <span className="hc-chat-thinking text-muted fst-italic">
                                                {thinkingMessage}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {!isStreaming && followUps.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2">
                                        {followUps.map((question) => (
                                            <button
                                                key={question}
                                                type="button"
                                                className="hc-chat-followup-chip"
                                                onClick={() => sendMessage(question, true)}
                                            >
                                                {question}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <form className="hc-chat-input-row d-flex align-items-center gap-2 p-3" onSubmit={handleSubmit}>
                        <textarea
                            ref={inputRef}
                            className="form-control hc-chat-textarea"
                            placeholder={placeholder}
                            rows={1}
                            value={inputValue}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                        />
                        {browserSupportsSpeechRecognition && (
                            <button
                                type="button"
                                className={`hc-chat-mic-btn d-inline-flex align-items-center justify-content-center border-0 flex-shrink-0 ${listening ? 'listening' : ''}`}
                                onClick={toggleListening}
                                aria-label="Toggle speech to text"
                                title="Speech to text"
                            >
                                {listening ? <MdMicOff size={20} /> : <MdMic size={20} />}
                            </button>
                        )}
                        <button
                            type="submit"
                            className="hc-chat-send-btn d-inline-flex align-items-center justify-content-center border-0 flex-shrink-0"
                            disabled={isStreaming || !inputValue.trim()}
                            aria-label="Send message"
                        >
                            <MdSend size={18} />
                        </button>
                    </form>
                </div>
            </div>

            {isOpen ? (
                <div
                    className="offcanvas-backdrop fade show hc-chat-backdrop"
                    aria-hidden="true"
                    style={{ zIndex: 1040 }}
                />
            ) : null}

            <EndConversationModal
                isOpen={showEndModal}
                onConfirm={handleConfirmEndConversation}
                onClose={() => {
                    if (isEndingConversation) return;
                    setShowEndModal(false);
                }}
                isLoading={isEndingConversation}
                errorMessage={endConversationError}
                allowEmail={Boolean(summaryEndpoint)}
            />
        </>
    );
}
